import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

// The Go Live modal. Go Live runs its checks one at a time (StreamingVendorControls'
// startStream) and this shows only the current one:
//   running - spinner, or a ring counting down the seconds the check may still take
//   passed  - green tick that draws itself; startStream then moves to the next check
//   failed  - red cross + what failed + why (detail); nothing goes live
// and, once every check passed and the stream is on, "You're live".
//
// check = { phase: "running" | "failed" | "live", number, total,
//           step: { id, label, status, text, detail?, deadline?, action? } }
// deadline = epoch ms the running check gives up at (countdown); none = answers at once.
// action   = a fix the admin can jump to from a failure (actionLabel / onAction).
// popupBlocked = the browser blocked Zoom's broadcast page: a red notice under the title,
//               whose button opens the page from the admin's click (onOpenBlockedPage).

const RING_RADIUS = 26;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

// Seconds left until `deadline`, re-read 4x a second while a countdown is shown.
function useSecondsLeft(deadline) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!deadline) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, [deadline]);
  if (!deadline) return null;
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

function RunningIcon({ deadline, startedAt }) {
  const secondsLeft = useSecondsLeft(deadline);
  if (secondsLeft == null) {
    return (
      <span
        aria-hidden="true"
        className="block w-16 h-16 rounded-full border-4 border-gray-200 border-t-emerald-500 animate-spin"
      />
    );
  }
  const totalMs = Math.max(1, deadline - startedAt);
  const leftFraction = Math.min(1, (secondsLeft * 1000) / totalMs);
  return (
    <span className="relative block w-16 h-16" title="Seconds left for this check">
      <svg className="w-16 h-16 -rotate-90" viewBox="0 0 60 60" aria-hidden="true">
        <circle cx="30" cy="30" r={RING_RADIUS} fill="none" strokeWidth="4" className="stroke-gray-200" />
        <circle
          cx="30"
          cy="30"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="4"
          strokeLinecap="round"
          className="stroke-emerald-500 transition-[stroke-dashoffset] duration-300 ease-linear"
          strokeDasharray={RING_LENGTH}
          strokeDashoffset={RING_LENGTH * (1 - leftFraction)}
        />
      </svg>
      <span
        className="absolute inset-0 flex items-center justify-center text-lg font-semibold text-gray-800 tabular-nums"
        aria-label={`${secondsLeft} seconds left`}
      >
        {secondsLeft}
      </span>
    </span>
  );
}

function ResultIcon({ ok }) {
  return (
    <span
      className={`animate-check-pop flex w-16 h-16 items-center justify-center rounded-full ${ok ? "bg-emerald-500" : "bg-red-500"}`}
      aria-hidden="true"
    >
      <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
        <path
          className="animate-check-draw"
          pathLength="1"
          strokeLinecap="round"
          strokeLinejoin="round"
          d={ok ? "M5 13l4 4L19 7" : "M6 6l12 12M18 6L6 18"}
        />
      </svg>
    </span>
  );
}

export function GoLiveCheckModal({
  check,
  onClose,
  onRetry,
  onAction,
  actionLabel,
  popupBlocked = false,
  onOpenBlockedPage,
}) {
  const { phase, number, total, step } = check;
  const running = phase === "running";

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const status = step?.status;
  // "Going live" (the step after every check) and "You're live": all checks are behind us.
  const pastChecks = phase === "live" || step?.id === "live";
  // The step counts as done for the dots once its tick is up.
  const done = pastChecks ? total : status === "passed" ? number : number - 1;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="go-live-check-title"
        className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h3 id="go-live-check-title" className="text-sm font-semibold text-gray-800">
            Go Live
          </h3>
          {!pastChecks && total > 0 && (
            <span className="text-[11px] font-medium text-gray-500 tabular-nums">
              Check {number} of {total}
            </span>
          )}
        </div>

        {popupBlocked && (
          <div
            role="alert"
            className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-left text-xs leading-relaxed text-red-700"
          >
            <p className="font-semibold">Pop-up blocked</p>
            <p className="mt-0.5">
              Your browser blocked Zoom&apos;s live-stream page, so Zoom can&apos;t start streaming. Open it
              here, or allow pop-ups for this site (the blocked pop-up icon in the address bar) and try
              again.
            </p>
            <button
              type="button"
              onClick={onOpenBlockedPage}
              className="mt-2 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-red-700"
            >
              Open Zoom page
            </button>
          </div>
        )}

        {step && (
          // Keyed by step: each new check slides in, its own status changes don't.
          <div
            key={step.id}
            role="status"
            aria-live="polite"
            className="animate-fade-slide-in mt-6 flex flex-col items-center text-center"
          >
            {status === "running" ? (
              <RunningIcon deadline={step.deadline} startedAt={step.startedAt} />
            ) : (
              <ResultIcon ok={status === "passed"} />
            )}
            <p className="mt-4 text-base font-semibold text-gray-900">{step.label}</p>
            <p
              className={`mt-1 text-sm ${
                status === "failed" ? "font-medium text-red-600" : status === "passed" ? "text-emerald-700" : "text-gray-500"
              }`}
            >
              {step.text}
            </p>
            {status === "failed" && step.detail && (
              <p className="mt-3 w-full rounded-lg border border-red-100 bg-red-50 px-3 py-2.5 text-left text-xs leading-relaxed text-red-800">
                {step.detail}
              </p>
            )}
          </div>
        )}

        {total > 0 && (
          <div className="mt-6 flex justify-center gap-1.5" aria-hidden="true">
            {Array.from({ length: total }, (_, i) => (
              <span
                key={i}
                className={`h-1.5 w-5 rounded-full transition-colors duration-300 ${
                  i < done
                    ? "bg-emerald-500"
                    : i === number - 1 && status === "failed"
                      ? "bg-red-500"
                      : i === number - 1
                        ? "bg-gray-400"
                        : "bg-gray-200"
                }`}
              />
            ))}
          </div>
        )}

        <div className="mt-6 flex gap-2">
          {running ? (
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
            >
              Cancel
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                Close
              </button>
              {phase === "failed" && step?.action && actionLabel && (
                <button
                  type="button"
                  onClick={onAction}
                  className="flex-1 rounded-lg border border-gray-900 px-3 py-2 text-sm font-medium text-gray-900 transition hover:bg-gray-50"
                >
                  {actionLabel}
                </button>
              )}
              {phase === "failed" && (
                <button
                  type="button"
                  autoFocus
                  onClick={onRetry}
                  className="flex-1 rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                  Try again
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default GoLiveCheckModal;
